import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import NoteIcon from '@material-ui/icons/Note';
import AddIcon from '@material-ui/icons/Add';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Chip from '@material-ui/core/Chip';
import { format, parseISO, isSameDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';

const useStyles = makeStyles(theme => ({
  card: {
    padding: 0,
    background: '#ffffff',
    borderRadius: 6,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
    marginBottom: theme.spacing(0.5),
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'all 0.2s ease-in-out',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
    },
  },
  cardAccepted: {
    backgroundColor: '#d4edda',
    borderLeft: '4px solid #28a745',
  },
  cardContent: {
    padding: theme.spacing(0.5),
  },
  contactHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.35),
    gap: theme.spacing(0.4),
  },
  avatar: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    backgroundColor: '#e9ecef',
    color: '#6c757d',
    fontSize: '0.65rem',
    fontWeight: 600,
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#212529',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: 0,
    lineHeight: 1,
  },
  ticketNumber: {
    fontSize: '0.6rem',
    fontWeight: 500,
    color: '#6c757d',
    lineHeight: 1,
    marginLeft: theme.spacing(0.3),
  },
  statusChip: {
    backgroundColor: '#28a745',
    color: '#fff',
    fontSize: '0.55rem',
    fontWeight: 600,
    height: 16,
    borderRadius: 8,
    textTransform: 'uppercase',
    '& .MuiChip-label': {
      padding: '0 3px',
    },
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.2),
    marginBottom: theme.spacing(0.25),
  },
  tagChip: {
    fontSize: '0.55rem',
    fontWeight: 500,
    height: 16,
    borderRadius: 8,
    '& .MuiChip-label': {
      padding: '0 3px',
    },
  },
  messageRow: {
    marginBottom: theme.spacing(0.25),
  },
  messageText: {
    fontSize: '0.65rem',
    color: '#6c757d',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontStyle: 'italic',
    lineHeight: 1,
  },
  valueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.25),
  },
  valueChip: {
    backgroundColor: '#e8f5e8',
    color: '#28a745',
    fontSize: '0.6rem',
    fontWeight: 600,
    height: 18,
    cursor: 'pointer',
    border: '1px solid #c3e6cb',
    borderRadius: 9,
    '&:hover': {
      backgroundColor: '#d4edda',
    },
    '& .MuiChip-icon': {
      color: '#28a745',
      fontSize: '0.7rem',
    },
  },
  valueChipEmpty: {
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
    fontSize: '0.6rem',
    fontWeight: 500,
    height: 18,
    cursor: 'pointer',
    border: '1px solid #dee2e6',
    borderRadius: 9,
    '&:hover': {
      backgroundColor: '#e9ecef',
    },
  },
  observationChip: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    fontSize: '0.55rem',
    fontWeight: 500,
    height: 18,
    cursor: 'pointer',
    border: '1px solid #ffeaa7',
    borderRadius: 9,
    maxWidth: '100%',
    '&:hover': {
      backgroundColor: '#ffeaa7',
    },
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '120px',
      padding: '0 3px',
    },
    '& .MuiChip-icon': {
      marginRight: '6px !important',
    },
  },
  observationChipEmpty: {
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
    fontSize: '0.55rem',
    fontWeight: 500,
    height: 18,
    cursor: 'pointer',
    border: '1px solid #dee2e6',
    borderRadius: 9,
    '&:hover': {
      backgroundColor: '#e9ecef',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(0.25),
    borderTop: '1px solid #f1f3f4',
    marginTop: theme.spacing(0.25),
    gap: theme.spacing(0.2),
  },
  connectionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.2),
    flex: 1,
    minWidth: 0,
  },
  connectionChip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    fontSize: '0.55rem',
    fontWeight: 500,
    height: 16,
    borderRadius: 8,
    maxWidth: '70px',
    '& .MuiChip-label': {
      padding: '0 3px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  userChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    fontSize: '0.55rem',
    fontWeight: 500,
    height: 16,
    borderRadius: 8,
    maxWidth: '60px',
    '& .MuiChip-label': {
      padding: '0 3px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  timeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.1),
    fontSize: '0.6rem',
    color: '#6c757d',
    whiteSpace: 'nowrap',
  },
  timeInfoUnread: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.1),
    fontSize: '0.6rem',
    color: '#28a745',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  actionButton: {
    padding: 2,
    color: '#6c757d',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      color: '#495057',
    },
  },
  actionButtonsRow: {
    display: 'flex',
    gap: theme.spacing(0.25),
    marginTop: theme.spacing(0.35),
    justifyContent: 'flex-end',
  },
  finishButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    fontSize: '0.6rem',
    fontWeight: 600,
    height: 20,
    borderRadius: 5,
    padding: '0 5px',
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: '#218838',
    },
    '& .MuiButton-startIcon': {
      marginRight: 1,
    },
  },
  returnButton: {
    backgroundColor: '#ffc107',
    color: '#000',
    fontSize: '0.6rem',
    fontWeight: 600,
    height: 20,
    borderRadius: 5,
    padding: '0 5px',
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: '#e0a800',
    },
    '& .MuiButton-startIcon': {
      marginRight: 1,
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
    },
  },
  dialogPaper: {
    borderRadius: '12px',
  },
  dialogButton: {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
  },
}));

const KanbanCard = ({ ticket, index, updateTicket, laneColor }) => {
  const classes = useStyles();
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [openObservation, setOpenObservation] = useState(false);
  const [newObservation, setNewObservation] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);

  const handleCardClick = () => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Monitora mudanças no ticket para re-renderizar
  React.useEffect(() => {
    // Apenas para forçar re-renderização quando ticket muda
  }, [ticket, forceUpdate]);

  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;
  
  const observationFieldIndex = customFields.findIndex(field => field.name === 'observacao');
  const observationField = observationFieldIndex !== -1 ? customFields[observationFieldIndex] : null;
  const observation = observationField ? observationField.value : null;

  const contactTags = ticket?.contact?.tags || [];

  // Obter o nome da instância WhatsApp
  const whatsappInstanceName = ticket.whatsapp?.name || ticket.contact?.whatsapp?.name || 'Sem instância';
  
  // Abreviar o nome da conexão se for muito longo
  const abbreviateConnectionName = (name) => {
    if (name.length > 15) {
      return name.substring(0, 12) + '...';
    }
    return name;
  };

  const handleOpenModal = () => {
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOpenObservationModal = () => {
    setNewObservation(observationField ? observationField.value : '');
    setOpenObservation(true);
  };

  const handleCloseObservationModal = () => {
    setOpenObservation(false);
  };

  const updateContactValue = async (contactId, value) => {
    try {
      const currentExtraInfo = ticket.contact.extraInfo || [];
      const updatedExtraInfo = [...currentExtraInfo];
      
      const valueIndex = updatedExtraInfo.findIndex(field => field.name === 'valor');
      if (valueIndex !== -1) {
        updatedExtraInfo[valueIndex] = { name: 'valor', value: value.toString() };
      } else {
        updatedExtraInfo.push({ name: 'valor', value: value.toString() });
      }

      await api.put(`/contacts/${contactId}`, {
        extraInfo: updatedExtraInfo,
      });
    } catch (error) {
      console.error('Erro ao atualizar o valor:', error);
    }
  };

  const updateContactObservation = async (contactId, observation) => {
    try {
      const currentExtraInfo = ticket.contact.extraInfo || [];
      const updatedExtraInfo = [...currentExtraInfo];
      
      const observationIndex = updatedExtraInfo.findIndex(field => field.name === 'observacao');
      if (observationIndex !== -1) {
        updatedExtraInfo[observationIndex] = { name: 'observacao', value: observation };
      } else {
        updatedExtraInfo.push({ name: 'observacao', value: observation });
      }

      await api.put(`/contacts/${contactId}`, {
        extraInfo: updatedExtraInfo,
      });
    } catch (error) {
      console.error('Erro ao atualizar a observação:', error);
    }
  };

  const removeContactValue = async () => {
    try {
      const currentExtraInfo = ticket.contact.extraInfo || [];
      const updatedExtraInfo = currentExtraInfo.filter(field => field.name !== 'valor');

      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: updatedExtraInfo,
      });

      if (valueFieldIndex !== -1) {
        customFields.splice(valueFieldIndex, 1);
      }

      updateTicket({ ...ticket });

    } catch (error) {
      console.error('Erro ao remover o valor:', error);
    }
  };

  const removeContactObservation = async () => {
    try {
      const currentExtraInfo = ticket.contact.extraInfo || [];
      const updatedExtraInfo = currentExtraInfo.filter(field => field.name !== 'observacao');

      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: updatedExtraInfo,
      });

      if (observationFieldIndex !== -1) {
        customFields.splice(observationFieldIndex, 1);
      }

      updateTicket({ ...ticket });

    } catch (error) {
      console.error('Erro ao remover a observação:', error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);

    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) {
        ticket.contact.extraInfo = [];
      }
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }

    updateTicket({ ...ticket });

    setOpen(false);
  };

  const handleSaveObservation = async () => {
    await updateContactObservation(ticket.contact.id, newObservation);

    if (observationField) {
      observationField.value = newObservation;
    } else {
      if (!ticket.contact.extraInfo) {
        ticket.contact.extraInfo = [];
      }
      ticket.contact.extraInfo.push({ name: 'observacao', value: newObservation });
    }

    updateTicket({ ...ticket });

    setOpenObservation(false);
  };

  const handleFinishTicket = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: 'closed',
      });
      updateTicket({ ...ticket, status: 'closed' });
    } catch (error) {
      console.error('Erro ao finalizar ticket:', error);
    }
  };

  const handleAcceptTicket = async () => {
    try {
      const currentExtraInfo = ticket.contact.extraInfo || [];
      const updatedExtraInfo = [...currentExtraInfo];
      
      const acceptedIndex = updatedExtraInfo.findIndex(field => field.name === 'aceito');
      if (acceptedIndex !== -1) {
        updatedExtraInfo[acceptedIndex] = { name: 'aceito', value: 'true' };
      } else {
        updatedExtraInfo.push({ name: 'aceito', value: 'true' });
      }

      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: updatedExtraInfo,
      });

      updateTicket({ 
        ...ticket, 
        contact: { 
          ...ticket.contact, 
          extraInfo: updatedExtraInfo 
        } 
      });
      
      setForceUpdate(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao aceitar ticket:', error);
    }
  };



  // Verifica se o ticket está em "Em Aberto" (sem tags)
  const isInOpenLane = ticket.tags.length === 0;
  
  // Verifica se o ticket foi aceito
  const acceptedField = customFields.find(field => field.name === 'aceito');
  const isAccepted = acceptedField && acceptedField.value === 'true';

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={`${classes.card}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            backgroundColor: '#ffffff',
            borderLeft: `4px solid ${laneColor || '#e9ecef'}`,
            opacity: 1,
            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
          }}
        >
          <div className={classes.cardContent}>
            {/* Header com avatar, nome e status */}
            <div className={classes.contactHeader}>
              <Avatar className={classes.avatar}>
                {(ticket.contact.name || 'S')[0].toUpperCase()}
              </Avatar>
              <div className={classes.contactInfo}>
                <Typography className={classes.cardTitle}>
                  {ticket.contact.name || 'Sem nome'} 
                  <span className={classes.ticketNumber}>#{ticket.id}</span>
                </Typography>
              </div>
              <Chip
                label="ABERTO"
                className={classes.statusChip}
                size="small"
              />
            </div>

            {/* Tags do contato */}
            {contactTags && Array.isArray(contactTags) && contactTags.length > 0 && (
              <div className={classes.tagsContainer}>
                {contactTags.map((tag) => {
                  if (!tag || !tag.id) return null;
                  return (
                    <Chip
                      key={tag.id}
                      label={tag.name || 'Tag sem nome'}
                      className={classes.tagChip}
                      size="small"
                      style={{
                        backgroundColor: tag.color || '#757575',
                        color: '#fff',
                      }}
                    />
                  );
                })}
              </div>
            )}

            {/* Última mensagem */}
            <div className={classes.messageRow}>
              <Typography className={classes.messageText}>
                {ticket.lastMessage || 'Sem mensagens'}
              </Typography>
            </div>

            {/* Valor e Observação */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <Chip
                  icon={opportunityValue !== null ? <AttachMoneyIcon /> : <AddIcon />}
                  label={opportunityValue !== null 
                    ? `R$ ${opportunityValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : 'Valor'}
                  onClick={handleOpenModal}
                  className={opportunityValue !== null ? classes.valueChip : classes.valueChipEmpty}
                  size="small"
                  onDelete={opportunityValue !== null ? () => removeContactValue() : undefined}
                  deleteIcon={<CloseIcon />}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Tooltip title={observation || 'Clique para adicionar observação'} arrow>
                  <Chip
                    icon={observation ? <NoteIcon /> : <AddIcon />}
                    label={observation 
                      ? (observation.length > 12 ? `${observation.substring(0, 12)}...` : observation)
                      : 'Obs'}
                    onClick={handleOpenObservationModal}
                    className={observation ? classes.observationChip : classes.observationChipEmpty}
                    size="small"
                    onDelete={observation ? () => removeContactObservation() : undefined}
                    deleteIcon={<CloseIcon />}
                  />
                </Tooltip>
              </div>
            </div>

            {/* Footer com conexão, usuário e tempo */}
            <div className={classes.footer}>
              <div className={classes.connectionInfo}>
                <Chip
                  label={abbreviateConnectionName(whatsappInstanceName)}
                  className={classes.connectionChip}
                  size="small"
                />
                {ticket.user && (
                  <Chip
                    label={ticket.user.name}
                    className={classes.userChip}
                    size="small"
                  />
                )}
              </div>
              <div className={Number(ticket.unreadMessages) > 0 ? classes.timeInfoUnread : classes.timeInfo}>
                <AccessTimeIcon style={{ fontSize: 10 }} />
                <Typography variant="caption">
                  {isSameDay(parseISO(ticket.updatedAt), new Date())
                    ? format(parseISO(ticket.updatedAt), 'HH:mm')
                    : format(parseISO(ticket.updatedAt), 'dd/MM')}
                </Typography>
              </div>
              <IconButton
                size="small"
                onClick={handleCardClick}
                className={classes.actionButton}
                style={{ padding: 2 }}
              >
                <VisibilityIcon style={{ fontSize: 14 }} />
              </IconButton>
              {!isInOpenLane && (
                <Tooltip title="Finalizar ticket">
                  <IconButton
                    size="small"
                    onClick={handleFinishTicket}
                    className={classes.actionButton}
                    style={{ padding: 2 }}
                  >
                    <CheckCircleIcon style={{ fontSize: 14, color: '#28a745' }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>

            {/* Botões de ação - Apenas para "Em Aberto" */}
            {isInOpenLane && !isAccepted && (
              <div className={classes.actionButtonsRow}>
                <Tooltip title="Aceitar ticket">
                  <Button
                    size="small"
                    className={classes.finishButton}
                    startIcon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                    onClick={handleAcceptTicket}
                  >
                    Aceitar
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
          <Dialog
            open={open}
            onClose={handleCloseModal}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>{valueField ? 'Editar' : 'Atribuir'} Valor da Oportunidade</DialogTitle>
            <DialogContent>
              <TextField
                label="Valor"
                type="number"
                fullWidth
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.textField}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="secondary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveValue}
                color="primary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openObservation}
            onClose={handleCloseObservationModal}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>{observationField ? 'Editar' : 'Adicionar'} Observação</DialogTitle>
            <DialogContent>
              <TextField
                label="Observação"
                multiline
                rows={4}
                fullWidth
                value={newObservation}
                onChange={(e) => setNewObservation(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.textField}
                placeholder="Digite suas observações sobre o cliente..."
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseObservationModal}
                color="secondary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveObservation}
                color="primary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
